import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import {
  I18NextLoadResult,
  I18NEXT_SERVICE,
  ITranslationService,
} from 'angular-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import HttpApi from 'i18next-http-backend';
import { firstValueFrom } from 'rxjs';
import { ApiConfiguration } from 'src/app/backend-client/api-configuration';
import { MxAppConfig } from 'src/app/shared/domains/common/interfaces';
import { environment } from 'src/environments/environment';
import {
  MxAppColorSchemeService,
  MxAppSidebarService,
  MxIpService,
  MxConstans,
  MxConstansService,
} from '.';
import { MxVersionCheckService } from './mx.version-check.service';

@Injectable({
  providedIn: 'root',
})
export class MxAppInitService {
  private config?: MxAppConfig;
  private ip?: string;

  get currentConfig(): MxAppConfig {
    return this.config;
  }

  get currentIp(): string {
    return this.ip;
  }

  constructor(
    @Inject(I18NEXT_SERVICE) private i18next: ITranslationService,
    private appColorSchemeService: MxAppColorSchemeService,
    private appSidebarService: MxAppSidebarService,
    private apiConfiguration: ApiConfiguration,
    private httpClient: HttpClient,
    private ipService: MxIpService,
    private versionCheckService: MxVersionCheckService
  ) {}

  async versionCheck(): Promise<any> {
    if (environment.production) {
      this.versionCheckService.initVersionCheck(
        `${document.getElementsByTagName('base')[0].href}version.json`
      );
    }
  }

  async loadConfig(): Promise<any> {
    this.config = await firstValueFrom(
      this.httpClient.get<MxAppConfig>(
        new URL(
          `assets/runtime-config.json?t=${Date.now()}`,
          document.baseURI
        ).toString()
      )
    );

    this.apiConfiguration.rootUrl = this.config.api.url;

    if (this.config.log.debug) {
      console.log(this.config);
    }
  }

  async loadIp(): Promise<any> {
    this.ip = (await firstValueFrom(this.ipService.getIPAddress())).ip;
  }

  async loadI18Next(): Promise<I18NextLoadResult> {
    const i18nextOptions = {
      whitelist: ['en', 'hu'],
      fallbackLng: ['en', 'hu'],
      debug: false,
      returnEmptyString: false,
      ns: [
        'translation',
        'primeng',
        'cookie',
        'page',
        'message',
        'button',
        'field',
        'validation',
        'enum',
      ],
      backend: {
        loadPath: `locales/{{lng}}/{{lng}}.{{ns}}.json?t=${new Date().getTime()}`,
      },
    };

    const languageDetector = new LanguageDetector(null, {
      lookupLocalStorage: `${MxConstansService.get(
        MxConstans.LOCAL_STORAGE_PREFIX
      )}-language`,
      lookupQuerystring: 'lng',
      order: [
        'querystring',
        'localStorage',
        `${MxConstansService.get(
          MxConstans.LOCAL_STORAGE_PREFIX
        )}-language-detector`,
      ],
    });

    languageDetector.addDetector({
      name: `${MxConstansService.get(
        MxConstans.LOCAL_STORAGE_PREFIX
      )}-language-detector`,
      lookup(options: any) {
        return 'hu';
      },

      cacheUserLanguage(lng: any, options: any) {},
    });

    return this.i18next.use(HttpApi).use(languageDetector).init(i18nextOptions);
  }

  async loadColorSheme(): Promise<void> {
    if (this.appColorSchemeService.colorScheme) {
      this.appColorSchemeService.changeStyleSheetsColor(
        `layout-css`,
        `layout-${this.appColorSchemeService.colorScheme}.css`,
        1
      );
    } else {
      this.appColorSchemeService.colorScheme = 'light';
    }
  }

  async loadSideBarSettings(): Promise<void> {
    if (!this.appSidebarService.sidebarActive) {
      this.appSidebarService.sidebarActive = false;
    }

    if (!this.appSidebarService.sidebarStatic) {
      this.appSidebarService.sidebarStatic = false;
    }
  }
}
